import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { authClient } from "@/auth/client";
import { authErrorMessage, PASSWORD_MAX_LENGTH } from "@/lib/auth-error";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Envelope,
  ArrowRight,
  CircleNotch,
  HandHeart,
  CheckCircle,
  Key,
  CaretLeft,
} from "@phosphor-icons/react";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    email: (typeof search.email === "string" ? search.email : "") as string,
  }),
});

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { email: prefillEmail } = Route.useSearch();

  const [step, setStep] = useState<"email" | "reset" | "success">("email");
  const [email, setEmail] = useState(prefillEmail);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const autoSubmittedOtpRef = useRef("");

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!email.trim()) return;
    setLoading(true);
    try {
      const result = await authClient.emailOtp.requestPasswordReset({
        email: email.trim(),
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "forgotPassword.requestError"));
        return;
      }
      setStep("reset");
    } catch {
      setError(t("forgotPassword.genericError"));
    } finally {
      setLoading(false);
    }
  }

  async function resetPassword() {
    setError("");
    if (otp.length < 6) return;
    if (password.length < 8) {
      setError(t("forgotPassword.passwordMinLength"));
      return;
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
      setError(t("auth.errors.passwordTooLong"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("forgotPassword.passwordMismatch"));
      return;
    }
    setLoading(true);
    try {
      const result = await authClient.emailOtp.resetPassword({
        email: email.trim(),
        otp: otp.trim(),
        password,
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "forgotPassword.resetError"));
        setOtp("");
        return;
      }
      setStep("success");
      setTimeout(() => navigate({ to: "/login" }), 2500);
    } catch {
      setError(t("forgotPassword.genericError"));
      setOtp("");
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    await resetPassword();
  }

  useEffect(() => {
    if (otp.length < 6) {
      autoSubmittedOtpRef.current = "";
      return;
    }
    if (step !== "reset" || loading || autoSubmittedOtpRef.current === otp) return;
    if (password.length < 8 || password !== confirmPassword) return;
    autoSubmittedOtpRef.current = otp;
    void resetPassword();
  }, [step, otp, password, confirmPassword, loading]);

  async function handleResend() {
    setError("");
    setResending(true);
    try {
      const result = await authClient.emailOtp.requestPasswordReset({
        email: email.trim(),
      });
      if (result.error) {
        setError(authErrorMessage(result.error, t, "forgotPassword.resendError"));
      }
    } catch {
      setError(t("forgotPassword.genericError"));
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div
        className="hidden w-[45%] flex-col justify-between p-10 lg:flex xl:p-14"
        style={{
          background:
            "linear-gradient(160deg, oklch(25% 0.05 84), oklch(30% 0.06 80))",
        }}
      >
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-lg bg-primary">
            <HandHeart className="size-4.5 text-primary-foreground" />
          </div>
          <span
            className="text-xl font-bold text-white"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Psihotron
          </span>
        </Link>

        <div>
          <h2
            className="text-3xl font-bold leading-tight text-white xl:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("forgotPassword.brandTitle")}
          </h2>
          <p
            className="mt-4 max-w-md text-sm leading-relaxed xl:text-base"
            style={{ color: "oklch(75% 0.02 84)" }}
          >
            {t("forgotPassword.brandSubtitle")}
          </p>
        </div>

        <p className="text-xs" style={{ color: "oklch(55% 0.02 84)" }}>
          {t("footer.copyright", { year: new Date().getFullYear() })}
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <Link
            to="/login"
            className="mb-8 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <CaretLeft className="size-4" />
            {t("forgotPassword.backToLogin")}
          </Link>

          <h1
            className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {step === "success"
              ? t("forgotPassword.successTitle")
              : t("forgotPassword.title")}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {step === "email" && t("forgotPassword.emailStepSubtitle")}
            {step === "reset" &&
              t("forgotPassword.resetStepSubtitle", { email })}
            {step === "success" && t("forgotPassword.successSubtitle")}
          </p>

          {error && (
            <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {step === "email" && (
            <form onSubmit={handleRequestOtp} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="fp-email">
                  {t("forgotPassword.emailLabel")}
                </Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder={t("forgotPassword.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="pl-10"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading || !email.trim()}
              >
                {loading ? (
                  <CircleNotch className="size-4 animate-spin" />
                ) : (
                  <ArrowRight className="size-4" />
                )}
                {loading
                  ? t("forgotPassword.sending")
                  : t("forgotPassword.sendCode")}
              </Button>
            </form>
          )}

          {step === "reset" && (
            <form onSubmit={handleResetPassword} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="fp-otp">{t("forgotPassword.otpLabel")}</Label>
                <OtpCodeInput
                  id="fp-otp"
                  value={otp}
                  onChange={(value) => {
                    setOtp(value);
                    setError("");
                  }}
                  invalid={!!error}
                  autoFocus
                  className="mt-2"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fp-password">
                  {t("forgotPassword.newPasswordLabel")}
                </Label>
                <Input
                  id="fp-password"
                  type="password"
                  placeholder={t("forgotPassword.newPasswordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  maxLength={PASSWORD_MAX_LENGTH}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fp-confirm">
                  {t("forgotPassword.confirmPasswordLabel")}
                </Label>
                <Input
                  id="fp-confirm"
                  type="password"
                  placeholder={t("forgotPassword.confirmPasswordPlaceholder")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  maxLength={PASSWORD_MAX_LENGTH}
                  autoComplete="new-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading || otp.length < 6 || password.length < 8}
              >
                {loading ? (
                  <CircleNotch className="size-4 animate-spin" />
                ) : (
                  <Key className="size-4" />
                )}
                {loading
                  ? t("forgotPassword.resetting")
                  : t("forgotPassword.resetButton")}
              </Button>
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={handleResend}
                  disabled={resending}
                  className="h-auto p-0 text-xs"
                >
                  {resending
                    ? t("forgotPassword.otpResending")
                    : t("forgotPassword.otpResend")}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setStep("email");
                    setOtp("");
                    setPassword("");
                    setConfirmPassword("");
                    setError("");
                  }}
                  className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                >
                  {t("forgotPassword.changeEmail")}
                </Button>
              </div>
            </form>
          )}

          {step === "success" && (
            <div className="mt-7 rounded-2xl border bg-emerald-50 p-6 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle className="size-6 text-emerald-600" />
              </div>
              <p className="mt-3 text-sm font-medium text-foreground">
                {t("forgotPassword.successMessage")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("forgotPassword.redirecting")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
